import { z } from "zod";

const boundedIdentifier = z.string().trim().min(1).max(256);
const boundedFileName = z.string().trim().min(1).max(512);
const boundedContentType = z.string().trim().min(1).max(256);
const boundedHash = z.string().trim().min(1).max(128);
const simpleTraceStateKeyPattern = /^[a-z][a-z0-9_*/-]{0,255}$/;
const multiTenantTraceStateKeyPattern = /^[a-z0-9][a-z0-9_*/-]{0,240}@[a-z][a-z0-9_*/-]{0,13}$/;

export const WorkflowTraceParentSchema = z
  .string()
  .regex(/^00-[0-9a-f]{32}-[0-9a-f]{16}-[0-9a-f]{2}$/)
  .refine((value) => value.slice(3, 35) !== "0".repeat(32))
  .refine((value) => value.slice(36, 52) !== "0".repeat(16));

export const WorkflowTraceStateSchema = z
  .string()
  .trim()
  .min(1)
  .max(512)
  .refine(isValidTraceState, "Invalid W3C tracestate");

function isValidTraceState(value: string): boolean {
  if (/[^\x20-\x7e]/.test(value)) return false;
  const members = value.split(",").map((member) => member.trim());
  if (members.length === 0 || members.length > 32 || members.some((member) => member.length === 0)) {
    return false;
  }

  const keys = new Set<string>();
  return members.every((member) => {
    const separator = member.indexOf("=");
    if (separator <= 0 || separator !== member.lastIndexOf("=")) return false;
    const key = member.slice(0, separator);
    const memberValue = member.slice(separator + 1);
    const validKey = simpleTraceStateKeyPattern.test(key) || multiTenantTraceStateKeyPattern.test(key);
    if (!validKey || keys.has(key)) return false;
    keys.add(key);
    return memberValue.length > 0
      && memberValue.length <= 256
      && !memberValue.startsWith(" ")
      && !memberValue.endsWith(" ")
      && !/[,=]/.test(memberValue)
      && !/[^\x20-\x7e]/.test(memberValue);
  });
}

const commonFields = {
  schemaVersion: z.literal(1),
  idempotencyKey: boundedIdentifier,
  traceparent: WorkflowTraceParentSchema,
  tracestate: WorkflowTraceStateSchema.optional(),
  tenantId: boundedIdentifier,
};

const metadataSchema = z.record(z.unknown()).optional();

const attachmentIngestSchema = z.object({
  ...commonFields,
  type: z.enum(["ATTACHMENT_INGEST", "EMAIL_INGEST"]),
  emailRecordId: boundedIdentifier,
  fileKey: boundedIdentifier,
  originalName: boundedFileName,
  contentType: boundedContentType,
  contentHash: boundedHash,
  source: boundedIdentifier.optional(),
  rawArchiveEntryId: boundedIdentifier.optional(),
  attachmentArchiveEntryIds: z.array(boundedIdentifier).max(100).optional(),
  textContent: z.string().max(1_000_000).optional(),
  metadata: metadataSchema,
}).strict();

const peppolIngestSchema = z.object({
  ...commonFields,
  type: z.literal("PEPPOL_INGEST"),
  traceId: boundedIdentifier.optional(),
  fileKey: boundedIdentifier,
  originalName: boundedFileName,
  contentType: boundedContentType,
  contentHash: boundedHash,
  source: z.literal("PEPPOL").optional(),
  metadata: metadataSchema,
}).strict();

const fileUploadSchema = z.object({
  ...commonFields,
  type: z.literal("FILE_UPLOAD"),
  documentId: boundedIdentifier.optional(),
  direction: z.enum(["INBOUND", "OUTBOUND"]).optional(),
  fileKey: boundedIdentifier,
  originalName: boundedFileName,
  contentType: boundedContentType,
  contentHash: boundedHash,
  uploadedBy: boundedIdentifier,
  metadata: metadataSchema,
}).strict();

export const InvoiceValidationQueueMessageSchema = z.object({ ...commonFields, type: z.literal("INVOICE_VALIDATION"), intentId: z.string().uuid(), runId: z.string().uuid() }).strict();
export type InvoiceValidationQueueMessage = z.infer<typeof InvoiceValidationQueueMessageSchema>;

export const InvoiceDeliveryQueueMessageSchema = z.object({...commonFields,type:z.literal("INVOICE_DELIVERY"),attemptKey:z.string().uuid(),deliveryId:z.string().uuid()}).strict();
export type InvoiceDeliveryQueueMessage = z.infer<typeof InvoiceDeliveryQueueMessageSchema>;

export const WorkflowQueueMessageSchema = z.discriminatedUnion("type", [
  attachmentIngestSchema,
  peppolIngestSchema,
  fileUploadSchema,
  InvoiceValidationQueueMessageSchema,
  InvoiceDeliveryQueueMessageSchema,
]);

export type WorkflowQueueMessage = z.infer<typeof WorkflowQueueMessageSchema>;
